/**
 * IDOR flow helpers. The UI collects API auth as a cookie string plus up to 5 custom header pairs;
 * `buildIdorAuthConfig` is the single point that maps those UI values to the IDOR backend contract.
 */

import { MESSAGE_SOURCES, MESSAGE_STATUS, MESSAGE_TYPES, getMessageText } from '../messageConfig';

const HEADER_RE = /^\s*authorization\s*$/i;

/**
 * The two pre-declaration IDOR stages. api-backend dispatches on `idor_stage` and forwards each to
 * gpt-engine `POST /idor/stage`.
 *  - AUTH_CONFIG:  repeatable auth+spec probe (first call carries base_url/auth/spec; retries re-send auth).
 *  - SCOPE_ACCESS: first entry (no candidates) fires candidate extraction; later calls deterministically
 *                  re-derive the object-access params for the confirmed scope.
 * Declaration is NOT a stage — it reuses the normal start (`declaration_checked:true` + `selected_candidates`).
 */
export const IDOR_STAGE = { AUTH_CONFIG: 'idor_auth_config', SCOPE_ACCESS: 'idor_scope_access' };

/**
 * Build a `/genpt/test` payload for an IDOR pre-declaration stage. Both stages ride the shared
 * `useStartGenPTEngine` mutation, discriminated by `test_type:'idor'` + `idor_stage`; api-backend
 * forwards to gpt-engine `POST /idor/stage`.
 */
export function idorStagePayload(stage, data = {}) {
  return { test_type: 'idor', idor_stage: stage, ...data };
}

/**
 * Emit a plain system text bubble (no widget). Used for IDOR flow intro framing text.
 * NOTE: IDOR chat *widgets* (auth → fetching → refine_scope → object_access → progress → findings) are
 * backend-driven and socket-pushed; the client no longer dispatches widget messages. The final
 * declaration step is client-driven (object-access finalize surfaces the shared DeclarationWidget).
 */
export function idorAddTextMessage(text) {
  window.dispatchEvent(new CustomEvent('genpt:addMessage', {
    detail: {
      type: 'idor_text',
      from: MESSAGE_SOURCES.SYSTEM,
      text,
      status: MESSAGE_STATUS.IN_PROGRESS,
      timestamp: Date.now(),
    },
  }));
}

/**
 * True when any candidate carries a filled object-access boundary (baseline_id / target_id).
 * This is the single client-side signal that object access was completed → surface the declaration.
 */
export function hasObjectAccessIds(selected) {
  return Array.isArray(selected) && selected.some((c) => c?.target_id || c?.baseline_id);
}

/**
 * Rebuild the Object-Access widget's `values` map ({ [param_name]: { baseline_id, target_id, location } })
 * from candidates that already carry IDs. Lets a reloaded/read-only Object-Access widget re-show the
 * entered Allowed/Not-Allowed IDs (its own typed state is otherwise lost on reload).
 */
export function seedObjectAccessValues(selected) {
  const values = {};
  (Array.isArray(selected) ? selected : []).forEach((c) => {
    if (!c?.param_name) return;
    if (!c.baseline_id && !c.target_id) return;
    values[c.param_name] = {
      baseline_id: c.baseline_id || '',
      target_id: c.target_id || '',
      ...(c.location ? { location: c.location } : {}),
    };
  });
  return values;
}

/**
 * Dispatch the client-side IDOR declaration message (mirrors {@link idorAddTextMessage}). The IDOR
 * declaration is surfaced by the frontend once object access is complete — no socket push — reusing the
 * shared DeclarationWidget rendering + `hasDeclaration` grouping.
 */
export function idorAddDeclarationMessage({ selected_candidates = [], excluded_candidates = [], base_url = null, coverage = null } = {}) {
  window.dispatchEvent(new CustomEvent('genpt:addMessage', {
    detail: {
      type: MESSAGE_TYPES.IDOR_DECLARATION,
      from: MESSAGE_SOURCES.SYSTEM,
      text: getMessageText({ type: MESSAGE_TYPES.IDOR_DECLARATION }, base_url),
      status: MESSAGE_STATUS.IN_PROGRESS,
      timestamp: Date.now(),
      widget: {
        type: MESSAGE_TYPES.IDOR_DECLARATION,
        data: { selected_candidates, excluded_candidates, base_url, ...(coverage ? { coverage } : {}) },
      },
    },
  }));
}

/** Normalize the `customHeaders` form value (array of {headerName, headerValue}) into {key,value}[]. */
export function normalizeCustomHeaders(customHeaders) {
  if (!Array.isArray(customHeaders)) return [];
  return customHeaders
    .map((h) => ({
      key: (h?.headerName ?? h?.key ?? '').trim(),
      value: (h?.headerValue ?? h?.value ?? '').trim(),
    }))
    .filter((h) => h.key && h.value);
}

/**
 * Map the UI's cookie + custom headers into the master-plan auth contract.
 *
 *  - a header `Authorization: Bearer <X>`  → { auth_method:'jwt',     auth_token:'<X>' }
 *  - a header `Authorization: Basic <X>`   → { auth_method:'basic',   auth_token:'<X>' }
 *  - otherwise the first custom header      → { auth_method:'api_key', api_key_header:key, auth_token:value }
 *  - the cookie string                      → supplementary_headers[{ key:'Cookie', value:<cookie> }]
 *  - every remaining custom header is appended to supplementary_headers (array order preserved).
 *
 * @returns {{auth_method, auth_token, api_key_header?, supplementary_headers}} or null when no usable credential.
 */
export function buildIdorAuthConfig({ cookieAuth = '', customHeaders = [] } = {}) {
  const headers = normalizeCustomHeaders(customHeaders);
  const cookie = (cookieAuth || '').trim();

  let auth_method = null;
  let auth_token = null;
  let api_key_header;
  const supplementary = [];

  // Prefer an explicit Authorization header to derive method + token.
  const authIdx = headers.findIndex((h) => HEADER_RE.test(h.key));
  if (authIdx !== -1) {
    const raw = headers[authIdx].value;
    const m = /^\s*(bearer|basic)\s+(.+)$/i.exec(raw);
    if (m) {
      auth_method = m[1].toLowerCase() === 'bearer' ? 'jwt' : 'basic';
      auth_token = m[2].trim();
    } else {
      // Bare Authorization value with no scheme — treat as a JWT-style bearer token.
      auth_method = 'jwt';
      auth_token = raw.trim();
    }
    // Remaining headers (everything except the consumed Authorization) become supplementary.
    headers.forEach((h, i) => {
      if (i !== authIdx) supplementary.push(h);
    });
  } else if (headers.length > 0) {
    // No Authorization header → treat the first custom header as an API key.
    auth_method = 'api_key';
    api_key_header = headers[0].key;
    auth_token = headers[0].value;
    headers.slice(1).forEach((h) => supplementary.push(h));
  }

  if (cookie) {
    supplementary.push({ key: 'Cookie', value: cookie });
    // Cookie-only auth (no header credential): the cookie IS the credential.
    if (!auth_method) {
      auth_method = 'api_key';
      api_key_header = 'Cookie';
      auth_token = cookie;
      supplementary.pop(); // don't double-send the cookie
    }
  }

  if (!auth_method || !auth_token) return null;

  return {
    auth_method,
    auth_token,
    ...(api_key_header ? { api_key_header } : {}),
    supplementary_headers: supplementary,
  };
}

/**
 * Extract the API-collection spec source for the IDOR pre-declaration payload from whatever the API
 * config flow has persisted (msg.metadata / session details `api_config`). The api-backend predeclaration
 * accepts EITHER `file_key` (uploaded spec) OR `api_collection_url` (remote spec) — never both.
 *
 * @returns {{file_key?:string, api_collection_url?:string, schema_endpoint_url?:string}} (possibly empty)
 */
export function extractSpecSource(...sources) {
  const configs = [];
  sources.forEach((s) => {
    if (!s) return;
    configs.push(s.api_config || s);
  });
  for (const cfg of configs) {
    if (!cfg || typeof cfg !== 'object') continue;
    const files = cfg.api_files || cfg.files;
    const fileKey = Array.isArray(files) && files.length ? files[0]?.file_key || files[0]?.fileKey : null;
    const urls = cfg.api_collection_urls || cfg.api_collection_url;
    const url = Array.isArray(urls) ? (urls.length ? String(urls[0]).trim() : null) : (urls ? String(urls).trim() : null);
    const schema = cfg.schema_endpoint_url || null;
    if (fileKey) {
      return { file_key: fileKey, ...(schema ? { schema_endpoint_url: schema } : {}) };
    }
    if (url) {
      return { api_collection_url: url, ...(schema ? { schema_endpoint_url: schema } : {}) };
    }
  }
  return {};
}

/**
 * Derive a coarse object/resource grouping label for a candidate endpoint, used to group rows in the
 * Refine-Scope widget (e.g. USERS, ORGS). Uses the first non-versioned, non-parametric path segment.
 */
export function deriveCandidateGroup(candidate) {
  const path = candidate?.path || candidate?.endpoint || '';
  const segments = String(path)
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((s) => !/^v\d+$/i.test(s) && !/^api$/i.test(s) && !s.startsWith('{'));
  const seg = segments[0] || 'ENDPOINTS';
  return seg.replace(/[-_]/g, ' ').toUpperCase();
}

/** Group candidates[] by derived object label, preserving first-seen order. */
export function groupCandidates(candidates = []) {
  const groups = new Map();
  candidates.forEach((c, idx) => {
    const key = deriveCandidateGroup(c);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ ...c, _idx: idx });
  });
  return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
}

/** Stable identity for a candidate (endpoint+method) — used for include/exclude selection sets. */
export function candidateKey(candidate) {
  return `${(candidate?.method || '').toUpperCase()} ${candidate?.endpoint || candidate?.path || ''}`;
}

/**
 * Sort + tag the scan_requirements params for the Object-Access widget: HIGH-IMPACT params float to
 * the top, then mandatory, preserving original order within a tier.
 */
export function orderObjectAccessParams(params = []) {
  return params
    .map((p, i) => ({ ...p, _idx: i }))
    .sort((a, b) => {
      const ha = a.impact === 'high' ? 1 : 0;
      const hb = b.impact === 'high' ? 1 : 0;
      if (ha !== hb) return hb - ha;
      const ma = a.mandatory ? 1 : 0;
      const mb = b.mandatory ? 1 : 0;
      if (ma !== mb) return mb - ma;
      return a._idx - b._idx;
    });
}

// IDs can be any string — no broad blocklist. Only whitespace and URL-delimiter characters are
// rejected because they break downstream request construction; everything else (incl. _ - . : + =)
// stays valid.
const INVALID_ID_CHARS_RE = /[ \t\r\n/?#%\\<>"'{}|^`]/;
const INVALID_ID_CHARS_MESSAGE = 'ID can\'t contain spaces or any of / ? # % \\ < > " \' { } | ^ `';

/**
 * Validate a single Allowed/Not-Allowed ID value's characters. An empty value is valid (it just
 * means "unconfigured" — handled separately by the pairing rules in the widget).
 *
 * @returns {string|null} an error message, or null when the value is valid.
 */
export function getIdValueError(value) {
  const raw = String(value ?? '');
  if (!raw) return null;
  return INVALID_ID_CHARS_RE.test(raw) ? INVALID_ID_CHARS_MESSAGE : null;
}

/** Count of params the user has fully configured (both Allowed + Not-Allowed IDs provided). */
export function countConfiguredParams(values = {}) {
  return Object.values(values).filter(
    (v) => v && String(v.baseline_id || '').trim() && String(v.target_id || '').trim()
  ).length;
}

/**
 * Count endpoints still in scope = phase1 candidates minus the excluded ones (matched by
 * candidateKey). Used for the "Refine Test Scope" completed-row summary so it reflects the
 * user's exclusions instead of the full candidate list.
 */
export function countInScopeEndpoints(candidates = [], excluded = []) {
  const excludedKeys = new Set((Array.isArray(excluded) ? excluded : []).map(candidateKey));
  return (Array.isArray(candidates) ? candidates : []).filter((c) => !excludedKeys.has(candidateKey(c))).length;
}

/**
 * Count DISTINCT params configured across selected_candidates (each carrying both Allowed +
 * Not-Allowed IDs). selected_candidates are endpoint×param pairs post-finalize, so the same
 * param on multiple endpoints counts once. Used for the "Object Access Test Data" completed-row
 * summary (endpoint×param pair count would over-report).
 */
export function countConfiguredParamsFromCandidates(selected = []) {
  const names = new Set();
  (Array.isArray(selected) ? selected : []).forEach((c) => {
    if (c?.param_name && String(c.baseline_id || '').trim() && String(c.target_id || '').trim()) {
      names.add(c.param_name);
    }
  });
  return names.size;
}

/**
 * Build the C3 `selected_candidates[]` for the declaration POST. For each in-scope candidate, pair it
 * with the param(s) the user supplied IDs for. A candidate whose required param has no Allowed ID is
 * dropped (the backend "safely skips" dependent endpoints — blank param ⇒ skipped).
 *
 * @param scopeCandidates in-scope candidate objects ({endpoint, method, params:[paramName], location?})
 * @param paramValues     { [param_name]: { baseline_id, target_id, location? } }
 */
export function buildSelectedCandidates(scopeCandidates = [], paramValues = {}) {
  const selected = [];
  scopeCandidates.forEach((c) => {
    const params = Array.isArray(c.params) && c.params.length
      ? c.params
      : (c.param_name ? [c.param_name] : []);
    params.forEach((paramName) => {
      const v = paramValues[paramName];
      if (!v || !String(v.baseline_id || '').trim() || !String(v.target_id || '').trim()) return;
      selected.push({
        endpoint: c.endpoint || c.path,
        method: (c.method || 'GET').toUpperCase(),
        param_name: paramName,
        location: v.location || c.location || 'path',
        baseline_id: String(v.baseline_id).trim(),
        target_id: String(v.target_id).trim(),
      });
    });
  });
  return selected;
}
